import wait from '../helpers/wait';
import accordion from './accordion';

interface IReviewsConfig {
  selectors: IReviewsSelectors,
  nodeLists: IReviewsNodeLists,
  htmlElements: IReviewsHTMLElements,
  hasReviews: boolean
}

interface IReviewsSelectors {
  main: string,
  newReviewBtn: string,
  newReviewSubmitBtn: string,
  starRating: string,
  starRatingCopyTo: string,
  reviews: string,
  caption: string,
  controls: string,
  bylines: string,
  page: string
}

interface IReviewsHTMLElements {
  starRating: HTMLElement,
  caption: HTMLElement
}

interface IReviewsNodeLists {
  main: NodeList,
  newReviewBtn: NodeList,
  newReviewSubmitBtn: NodeList,
  starRatingCopyTo: NodeList,
  reviews: NodeList,
  controls: NodeList,
  bylines: NodeList,
  page: NodeList
}

class Reviews {

  private config: IReviewsConfig;

  constructor() {
    this.config = {
      hasReviews: false,
      nodeLists: {
        main: null,
        newReviewBtn: null,
        newReviewSubmitBtn: null,
        starRatingCopyTo: null,
        reviews: null,
        controls: null,
        bylines: null,
        page: null
      },
      htmlElements: {
        starRating: null,
        caption: null
      },
      selectors: {
        main: '#shopify-product-reviews',
        newReviewBtn: '.spr-summary-actions-newreview',
        newReviewSubmitBtn: '.spr-button-primary',
        starRating: '.spr-starrating',
        starRatingCopyTo: '[data-star-rating-copy-to]',
        reviews: '.spr-review',
        caption: '.spr-summary-caption',
        page: '.spr-pagination-page a',
        controls: '.spr-pagination-next a, .spr-pagination-prev a',
        bylines: '.spr-review-header-byline'
      }
    }
  }

  async init() {
    await wait.forNodeList(this.config.selectors.newReviewSubmitBtn, 100, 10);
    await wait.forNodeList(this.config.selectors.starRating, 100, 10);
    await wait.forNodeList(this.config.selectors.reviews, 100, 10);
    await wait.forNodeList(this.config.selectors.caption, 100, 10);
    await wait.forNodeList(this.config.selectors.bylines, 100, 10);
    this.config.nodeLists.main = document.querySelectorAll(this.config.selectors.main);
    this.config.nodeLists.main.forEach((htmlElement: HTMLElement) => this.load(htmlElement));
  }

  load(htmlElement: HTMLElement) {
    this.update(htmlElement);
    if (this.config.hasReviews) this.renderReviewDisplay();
    this.addEventListeners();
  }

  update(htmlElement: HTMLElement) {
    this.config.htmlElements.starRating = htmlElement.querySelector(this.config.selectors.starRating);
    this.config.htmlElements.caption = htmlElement.querySelector(this.config.selectors.caption);
    this.config.nodeLists.newReviewBtn = htmlElement.querySelectorAll(this.config.selectors.newReviewBtn);
    this.config.nodeLists.newReviewSubmitBtn = htmlElement.querySelectorAll(this.config.selectors.newReviewSubmitBtn);
    this.config.nodeLists.starRatingCopyTo = document.querySelectorAll(this.config.selectors.starRatingCopyTo);
    this.config.nodeLists.reviews = htmlElement.querySelectorAll(this.config.selectors.reviews);
    this.config.nodeLists.controls = htmlElement.querySelectorAll(this.config.selectors.controls);
    this.config.nodeLists.bylines = htmlElement.querySelectorAll(this.config.selectors.bylines);
    this.config.nodeLists.page = htmlElement.querySelectorAll(this.config.selectors.page);
    this.config.hasReviews = this.config.nodeLists.reviews.length > 0;
  }

  renderReviewDisplay() {
    this.updateHeadingText();
    this.reviewCount();
    this.moveStars();
    this.updateBylines();
    this.addReviewClass();
    this.removePageHrefs();
    this.config.htmlElements.caption.remove();
    this.config.htmlElements.starRating.remove();
  }

  updateBylines() {
    this.config.nodeLists.bylines.forEach((htmlElement: HTMLElement) => {
      const byline = htmlElement.innerText.split('on');
      const date = byline[1].trim();
      htmlElement.innerText = date;
      htmlElement.style.opacity = '1';
    });
  }

  addReviewClass() {
    this.config.nodeLists.main.forEach((htmlElement: HTMLElement) => htmlElement.classList.add('spr--has-reviews'));
  }

  removePageHrefs() {
    this.config.nodeLists.page.forEach((htmlElement: HTMLAnchorElement) => htmlElement.href = 'javascript: void(0)');
    this.config.nodeLists.controls.forEach((htmlElement: HTMLAnchorElement) => htmlElement.href = 'javascript: void(0)');
    this.config.nodeLists.newReviewBtn.forEach((htmlElement: HTMLAnchorElement) => htmlElement.href = 'javascript: void(0)');
  }

  updateHeadingText() {
    const headingText = document.createElement('span');
    this.config.nodeLists.starRatingCopyTo.forEach((htmlElement: HTMLElement) => {
      headingText.innerText = htmlElement.innerText;
      htmlElement.innerText = '';
      htmlElement.append(headingText);
    });
  }

  reviewCount() {
    const caption = this.config.htmlElements.caption.cloneNode(true) as HTMLElement;
    const captionChildNode = caption.childNodes[0] as HTMLElement;
    caption.innerHTML = `(${ captionChildNode.innerText })`;
    this.config.nodeLists.starRatingCopyTo.forEach((htmlElement: HTMLElement) => htmlElement.append(caption));

  }

  moveStars() {
    this.config.nodeLists.starRatingCopyTo.forEach((htmlElement: HTMLElement) => htmlElement.append(this.config.htmlElements.starRating.cloneNode(true)));
  }

  addEventListeners() {
    this.config.nodeLists.newReviewBtn.forEach(htmlElement => htmlElement.addEventListener('click', this.handleClick.bind(this, 0)));
    this.config.nodeLists.page.forEach(htmlElement => htmlElement.addEventListener('click', this.handleControlClick.bind(this, 200)));
    this.config.nodeLists.controls.forEach(htmlElement => htmlElement.addEventListener('click', this.handleControlClick.bind(this, 200)));
    this.config.nodeLists.newReviewSubmitBtn.forEach(htmlElement => htmlElement.addEventListener('click', this.handleClick.bind(this, 200)));
  }

  handleControlClick(delay: number) {
    setTimeout(function() {
      this.config.nodeLists.main.forEach((htmlElement: HTMLElement) => {
        this.update(htmlElement);
        this.updateBylines();
        this.removePageHrefs();
        this.config.nodeLists.page.forEach((htmlElement: HTMLElement) => htmlElement.addEventListener('click', this.handleControlClick.bind(this, 200)));
        this.config.nodeLists.controls.forEach((htmlElement: HTMLElement) => htmlElement.addEventListener('click', this.handleControlClick.bind(this, 200)));
        this.config.nodeLists.newReviewSubmitBtn.forEach((htmlElement: HTMLElement) => htmlElement.addEventListener('click', this.handleClick.bind(this, 200)));
        accordion.load();
      });
    }.bind(this, accordion), delay);
  }

  handleClick(delay: number) {
    setTimeout(accordion.load.bind(accordion), delay);
  }
}

export default new Reviews();